// src/components/ReplyForm.js
import React, { useEffect, useState } from 'react';
import '../styles/ReplyForm.css';
import '../styles/common.css';
import LimitedTextarea from './LimitedTextarea';
import { apiPost, apiPut, bootstrapBffSession } from '../lib/apiClient';
import { getRecaptchaToken } from '../lib/recaptcha';
import { useAnonConsent } from '../context/AnonConsentContext';

export default function ReplyForm({
  postId,
  reply,
  isEditMode = false,
  onUpdate,
  onNewReply,
  onCancelEdit,
  inputRef,
  hideLabel = false
}) {
  const [content, setContent] = useState(reply?.content || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { consented } = useAnonConsent();

  // Ensure stable server-issued identity cookie exists
  useEffect(() => {
    bootstrapBffSession().catch(() => {});
  }, []);

  const handleInsert = async () => {
    setLoading(true);
    setError(null);
    try {
      await bootstrapBffSession();

      // Ensure we send a NUMBER, not a string/undefined
      const payload = { postId: Number(postId), content: content.trim() };

      let recaptchaToken = null;
      try {
        recaptchaToken = await getRecaptchaToken('reply_create');
      } catch (err) {
        setError(err?.message || 'reCAPTCHA verification failed. Please retry.');
        return;
      }

      const options = recaptchaToken ? { headers: { 'X-Recaptcha-Token': recaptchaToken } } : undefined;
      const data = await apiPost('/replies', payload, options);

      setContent('');
      onNewReply?.(data);
    } catch (e) {
       // Inline moderation error (HTTP 422)
      const isMod = e && e.status === 422;
      const msg = e?.msg || e?.message || (isMod ? 'Content not allowed.' : 'Failed to create post');
      setError(msg);
      console.error('Error creating post:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!reply) return;
    setLoading(true);
    setError(null);
    try {
      await bootstrapBffSession();
      let recaptchaToken = null;
      try {
        recaptchaToken = await getRecaptchaToken('reply_update');
      } catch (err) {
        setError(err?.message || 'reCAPTCHA verification failed. Please retry.');
        return;
      }

      const options = recaptchaToken ? { headers: { 'X-Recaptcha-Token': recaptchaToken } } : undefined;
      const data = await apiPut(`/replies/${reply.id}`, { content: content.trim() }, options);
      if (data) onUpdate?.(data);
    } catch (err) {
      const msg = err?.msg || err?.message || 'Failed to update reply';
      setError(msg);
      console.error('Update failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    if (!consented) {
      setError('Read-only mode: enable anonymous ID participation on the ID Policy page to reply.');
      return;
    }
    // Guard: postId must be a finite number
    if (!Number.isFinite(Number(postId))) {
      setError('Missing or invalid postId.');
      return;
    }
    isEditMode ? await handleUpdate() : await handleInsert();
  };

  return (
    <form onSubmit={handleSubmit} className="reply-form" style={{ marginTop: '0.5rem' }}>
      {!hideLabel && (
        <label htmlFor={`reply-${postId}`} className="field-label" style={{ marginTop: 0 }}>
          Your Reply
        </label>
      )}
      <LimitedTextarea
        id={`reply-${postId}`}
        placeholder="Write your reply…"
        value={content}
        onChange={(e) => { setContent(e.target.value); if (error) setError(null); }}
        required
        rows={1}
        minRows={1}
        maxRows={5}
        autoGrow
        max={240}
        className="comment-textarea"
        wrapperClassName="comment-box comment-box--compact"
        counterClassName="comment-char-counter"
        inputRef={inputRef}
      />
      <div className="form-actions" style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', justifyContent: 'flex-end' }}>
        <button type="submit" className="btn btn-primary btn-xs" disabled={loading || !!error || !consented}>
          {loading ? 'Saving…' : isEditMode ? 'Save' : consented ? 'Post Reply' : 'Enable participation to reply'}
        </button>
        {isEditMode && (
          <button type="button" onClick={onCancelEdit} className="btn btn-sm">
            Cancel
          </button>
        )}
      </div>
      {error && (
        <div className="error" style={{ marginTop: '0.5rem' }}>
          {error}
        </div>
      )}
      {!consented && (
        <div className="muted" style={{ marginTop: '0.5rem' }}>
          Read-only mode: visit the <a href="/privacy/anonymous-id">ID Policy</a> page to enable participation.
        </div>
      )}
    </form>
  );
}
